import { useState, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useGetUserQuery, useGetUserActivityQuery, useGetUserStatsQuery, useGetAiConversationsQuery, useGetOnThisDayQuery, useGetWritingStatsQuery, useGetAiUsageQuery } from '../store/api/usersApi'
import { useGetDiscussionsQuery, useTogglePinDiscussionMutation } from '../store/api/discussionsApi'
import { useFollowUserMutation, useUnfollowUserMutation, useGetFollowStatusQuery } from '../store/api/usersApi'
import DiscussionCard from '../components/DiscussionCard'
import ContributionGraph from '../components/ui/ContributionGraph'
import { timeAgoLong, timeAgoShort, contentPreview } from '../lib/avatar'
import { DISCUSSIONS_PAGE_SIZE, PINNING_ENABLED, PIN_COLOR, PIN_COLOR_BG, COLOR_ACCENT_ROLE_BG, CATEGORY_BADGE_PREVIEW_MAX, AI_REPLY_PREVIEW_TRUNCATE } from '../lib/config'
import Avatar from '../components/ui/Avatar'
import { computeAchievements, computeStreak, type Achievement } from '../lib/achievements'
import FollowListModal from '../components/ui/FollowListModal'
import { useInfiniteScroll } from '../hooks/useInfiniteScroll'
import { Button, Card, EmptyState, Spinner, Tabs, RefreshButton, SentinelLoader } from '../components/ui'
import s from '../styles/pages/ProfilePage.module.css'

type ProfileTab = 'discussions' | 'stats' | 'achievements' | 'ai' | 'insights'
type InsightTab = 'journey' | 'writing' | 'onthisday' | 'active'

function PinnableCard({ d, isOwner, onTogglePin }: {
  d: any; isOwner: boolean; onTogglePin: (id: string) => Promise<any>
}) {
  const pinned = Array.isArray(d.pins) ? d.pins.length > 0 : false
  return (
    <div className={s.pinnableWrap}>
      <DiscussionCard
        id={d.id} title={d.title}
        contentPreview={contentPreview(d.content)}
        author={{ id: d.author.id, displayName: d.author.profile?.displayName ?? 'User', avatarUrl: d.author.profile?.avatarUrl, flair: d.author.profile?.flair }}
        categories={d.categories} tags={d.tags.map((t: any) => t.tag.name)}
        viewCount={d.viewCount} commentCount={d.commentCount} voteScore={d.voteScore}
        createdAt={d.createdAt} isPinned={PINNING_ENABLED && pinned}
      />
      {PINNING_ENABLED && isOwner && (
        <button
          onClick={(e) => { e.preventDefault(); void onTogglePin(d.id) }}
          title={pinned ? 'Unpin' : 'Pin to my list'}
          className={s.pinBtn}
          style={{
            background: pinned ? PIN_COLOR_BG : 'var(--bg-elevated)',
            border: `1px solid ${pinned ? PIN_COLOR : 'var(--border)'}`,
            color: pinned ? PIN_COLOR : 'var(--text-muted)',
            fontWeight: pinned ? 600 : 400,
          }}
        >{pinned ? 'Unpin' : '📌 Pin'}</button>
      )}
    </div>
  )
}

function AchievementBadge({ a }: { a: Achievement }) {
  const tierColors = {
    bronze: { bg: 'rgba(180,83,9,0.12)', border: 'rgba(180,83,9,0.3)', text: '#b45309' },
    silver: { bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.3)', text: '#6b7280' },
    gold:   { bg: 'rgba(234,179,8,0.12)', border: 'rgba(234,179,8,0.3)', text: 'var(--warning)' },
  }
  const colors = tierColors[a.tier]
  return (
    <div
      title={a.description}
      className={s.achievementBadge}
      style={{
        background: a.unlocked ? colors.bg : 'var(--bg-elevated)',
        border: `1px solid ${a.unlocked ? colors.border : 'var(--border)'}`,
        opacity: a.unlocked ? 1 : 0.4,
        filter: a.unlocked ? 'none' : 'grayscale(1)',
      }}
    >
      <span className={s.achievementIcon}>{a.icon}</span>
      <span className={s.achievementLabel} style={{ color: a.unlocked ? colors.text : 'var(--text-muted)' }}>{a.label}</span>
    </div>
  )
}

// ── Feature 11: Journey milestone ────────────────────────────
function JourneyMilestone({ icon, label, sub, date, last, pending }: {
  icon: string; label: string; sub?: string; date?: string; last?: boolean; pending?: boolean
}) {
  return (
    <div className={`${s.journeyMilestoneWrap} ${!last ? s.withPadding : ''}`}>
      {!last && (
        <div className={s.journeyConnector} style={{
          background: pending
            ? 'repeating-linear-gradient(to bottom, var(--border) 0px, var(--border) 4px, transparent 4px, transparent 8px)'
            : 'linear-gradient(to bottom, var(--accent-primary), rgba(124,58,237,0.2))',
        }} />
      )}
      <div className={`${s.journeyIconBubble} ${pending ? s.pending : s.achieved}`}>{icon}</div>
      <div className={s.journeyBody}>
        <div className={`${s.journeyLabelRow} ${pending ? s.pending : ''}`}>
          {label}
          {pending && <span className={s.journeyNextBadge}>Next</span>}
        </div>
        {sub && <div className={s.journeySub}>{sub}</div>}
        {date && <div className={s.journeyDate}>{date}</div>}
      </div>
    </div>
  )
}

export default function ProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: me } = useAppSelector((s) => s.auth)
  const userId = id ?? me?.id ?? ''
  const isOwner = me?.id === userId

  const [activeTab, setActiveTab] = useState<ProfileTab>('discussions')
  const [insightTab, setInsightTab] = useState<InsightTab>('journey')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [aiCursor, setAiCursor] = useState<string | undefined>(undefined)
  const [followModal, setFollowModal] = useState<'followers' | 'following' | null>(null)

  const { data: userData, isLoading } = useGetUserQuery(userId, { skip: !userId })
  const { data: discussionsData, isFetching, refetch: refetchDiscussions } = useGetDiscussionsQuery(
    { authorId: userId, limit: DISCUSSIONS_PAGE_SIZE, cursor },
    { skip: !userId || activeTab !== 'discussions' }
  )
  const { data: activityData, isLoading: activityLoading } = useGetUserActivityQuery(
    { id: userId, year: selectedYear }, { skip: !userId }
  )
  const { data: statsData, refetch: refetchStats, isFetching: statsFetching } = useGetUserStatsQuery(userId, { skip: !userId || (activeTab !== 'stats' && activeTab !== 'insights') })
  const { data: aiUsageData } = useGetAiUsageQuery(undefined, { skip: !isOwner || activeTab !== 'stats', pollingInterval: 60000 })
  const { data: aiData, isFetching: aiFetching, refetch: refetchAi } = useGetAiConversationsQuery(
    { userId, cursor: aiCursor }, { skip: !userId || activeTab !== 'ai' }
  )
  const { data: onThisDayData, refetch: refetchInsights, isFetching: insightsFetching } = useGetOnThisDayQuery(userId, { skip: !userId || activeTab !== 'insights' })
  const { data: writingStatsData } = useGetWritingStatsQuery(userId, { skip: !userId || activeTab !== 'insights' })
  const [togglePin] = useTogglePinDiscussionMutation()
  const [followUser] = useFollowUserMutation()
  const [unfollowUser] = useUnfollowUserMutation()
  const { data: followStatusData } = useGetFollowStatusQuery(userId, { skip: !userId || isOwner })
  const isFollowing = followStatusData?.isFollowing ?? false

  // Infinite scroll for discussions tab
  const loadMoreDiscs = useCallback(() => {
    if (discussionsData?.hasMore && discussionsData.nextCursor) setCursor(discussionsData.nextCursor)
  }, [discussionsData])
  const discSentinelRef = useInfiniteScroll({ hasMore: discussionsData?.hasMore ?? false, isFetching, onLoadMore: loadMoreDiscs })

  // Infinite scroll for AI conversations tab
  const loadMoreAi = useCallback(() => {
    if (aiData?.hasMore && aiData.nextCursor) setAiCursor(aiData.nextCursor)
  }, [aiData])
  const aiSentinelRef = useInfiniteScroll({ hasMore: aiData?.hasMore ?? false, isFetching: aiFetching, onLoadMore: loadMoreAi })

  const user = userData?.user
  const discussions = discussionsData?.data ?? []
  const name = user?.profile?.displayName ?? 'User'
  const activity = activityData?.activity ?? {}
  const streak = computeStreak(activity)
  const achievements = computeAchievements({
    discussions: (user as any)?._count?.discussions ?? 0,
    followers: (user as any)?._count?.followers ?? 0,
    following: (user as any)?._count?.following ?? 0,
    totalContributions: activityData?.totalContributions ?? 0,
    streak, activity,
  })
  const unlockedCount = achievements.filter(a => a.unlocked).length

  // Writing stats — now computed server-side over all posts, not just 50
  const writingStats = writingStatsData?.stats ?? null

  // ── Feature 11: Journey milestones ──────────────────────────
  const milestones = (() => {
    const joinDate = user?.createdAt ? new Date(user.createdAt) : null
    const totalDiscs = (user as any)?._count?.discussions ?? 0
    const totalFollowers = (user as any)?._count?.followers ?? 0
    const totalContribs = activityData?.totalContributions ?? 0
    const items: { icon: string; label: string; sub?: string; date?: string; achieved: boolean; order: number }[] = [
      { icon: '🌱', label: 'Joined Ekkam', sub: 'Your journey begins', date: joinDate ? joinDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : undefined, achieved: true, order: 0 },
      { icon: '✍️', label: 'First Discussion', sub: 'Posted your first question', achieved: totalDiscs >= 1, order: 1 },
      { icon: '💬', label: '10 Contributions', sub: 'Getting active on the platform', achieved: totalContribs >= 10, order: 2 },
      { icon: '🔥', label: 'First Streak', sub: 'Contributed 3 days in a row', achieved: streak >= 3 || totalContribs >= 20, order: 3 },
      { icon: '👥', label: 'First Follower', sub: 'Someone found your posts valuable', achieved: totalFollowers >= 1, order: 4 },
      { icon: '📝', label: '10 Discussions', sub: 'Becoming a regular contributor', achieved: totalDiscs >= 10, order: 5 },
      { icon: '💯', label: '100 Contributions', sub: 'A true community member', achieved: totalContribs >= 100, order: 6 },
      { icon: '⭐', label: '5 Followers', sub: 'Building your audience', achieved: totalFollowers >= 5, order: 7 },
      { icon: '🏆', label: 'Legend', sub: '500 contributions milestone', achieved: totalContribs >= 500, order: 8 },
    ]
    return items.filter(m => m.achieved)
  })()

  if (isLoading) return (
    <div className={s.loadingState}>Loading profile...</div>
  )
  if (!user) return (
    <div className={s.notFoundState}>
      <div className={s.notFoundIcon}>👤</div>
      <div>User not found</div>
      <Link to="/" className={s.notFoundLink}>Go home →</Link>
    </div>
  )

  const isRefreshing = activeTab === 'discussions' ? isFetching
    : activeTab === 'stats' ? statsFetching
    : activeTab === 'ai' ? aiFetching
    : activeTab === 'insights' ? insightsFetching
    : false
  const handleRefresh = () => {
    if (activeTab === 'discussions') { setCursor(undefined); void refetchDiscussions() }
    else if (activeTab === 'stats') void refetchStats()
    else if (activeTab === 'ai') { setAiCursor(undefined); void refetchAi() }
    else if (activeTab === 'insights') void refetchInsights()
  }

  return (
    <div>
      <Card style={{ marginBottom: '1.25rem' }}>
        <div className={s.headerInner}>
          <div className={s.avatarWrap}>
            <Avatar name={name} avatarUrl={user.profile?.avatarUrl} size={72} />
            {streak >= 2 && (
              <div className={s.streakBadge}>
                {streak >= 30 ? '🔥🔥🔥' : streak >= 14 ? '🔥🔥' : '🔥'} {streak}
              </div>
            )}
          </div>
          <div className={s.headerInfo}>
            <div className={s.nameRow}>
              <h1 className={s.nameHeading}>{name}</h1>
              {user.profile?.flair && (
                <span style={{ fontSize: '0.75rem', background: 'rgba(124,58,237,0.1)', color: 'var(--accent-primary)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '20px', padding: '0.15rem 0.6rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {user.profile.flair}
                </span>
              )}
              {user.roles.includes('admin') && (
                <span className={s.adminBadge} style={{ background: COLOR_ACCENT_ROLE_BG, color: 'var(--accent-primary)' }}>Admin</span>
              )}
              {unlockedCount > 0 && (
                <span className={s.badgesCount}>🏆 {unlockedCount} badge{unlockedCount !== 1 ? 's' : ''}</span>
              )}
            </div>
            {user.profile?.bio && <p className={s.bio}>{user.profile.bio}</p>}
            <div className={s.joinedDate}>Joined {timeAgoLong(user.createdAt)}</div>
            <div className={s.statsRow}>
              {([
                { label: 'Discussions',   value: (user as any)._count?.discussions ?? 0, onClick: undefined },
                { label: 'Followers',     value: (user as any)._count?.followers   ?? 0, onClick: () => setFollowModal('followers') },
                { label: 'Following',     value: (user as any)._count?.following   ?? 0, onClick: () => setFollowModal('following') },
                { label: 'Contributions', value: activityData?.totalContributions ?? 0,  onClick: undefined },
              ] as { label: string; value: number; onClick?: () => void }[]).map(({ label, value, onClick }, i, arr) => (
                <div
                  key={label}
                  onClick={onClick}
                  className={s.statItem}
                  style={{ cursor: onClick ? 'pointer' : 'default', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}
                  onMouseEnter={(e) => { if (onClick) (e.currentTarget as HTMLDivElement).style.opacity = '0.75' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1' }}
                >
                  <span className={`${s.statValue} ${onClick ? s.statValueClickable : ''}`}>{value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}</span>
                  <span className={s.statLabel}>{label}</span>
                </div>
              ))}
            </div>
          </div>
          {isOwner ? (
            <Link to="/settings" className={s.editProfileLink}>✏️ Edit profile</Link>
          ) : me ? (
            <Button variant={isFollowing ? 'ghost' : 'primary'} onClick={() => isFollowing ? void unfollowUser(userId) : void followUser(userId)}>
              {isFollowing ? 'Following' : '+ Follow'}
            </Button>
          ) : null}
        </div>
      </Card>

      {/* ── Contribution graph ── */}
      <ContributionGraph
        userId={userId} activity={activity}
        totalContributions={activityData?.totalContributions ?? 0}
        isLoading={activityLoading} selectedYear={selectedYear}
        onYearChange={(y) => setSelectedYear(y)}
      />

      <Tabs
        tabs={[
          { key: 'discussions',  label: '💬 Discussions' },
          { key: 'stats',        label: '📊 Stats' },
          { key: 'achievements', label: '🏆 Badges', badge: unlockedCount > 0 ? unlockedCount : undefined },
          { key: 'ai',          label: '🤖 AI' },
          { key: 'insights',    label: '✨ Insights' },
        ]}
        active={activeTab}
        onChange={(t) => { setActiveTab(t as ProfileTab); setCursor(undefined); setAiCursor(undefined) }}
        rightSlot={
          <RefreshButton
            onClick={handleRefresh}
            loading={isRefreshing}
            title={`Refresh ${activeTab}`}
          />
        }
      />

      {/* ── Discussions tab ── */}
      {activeTab === 'discussions' && (
        <>
          <div className={s.discTabHeader}>
            <span className={s.discTabTitle}>Recent Discussions</span>
          </div>
          {discussions.length === 0 && !isFetching && (
            <EmptyState icon="💬" title="No discussions yet" />
          )}
          {discussions.map((d) => (
            <PinnableCard key={d.id} d={d} isOwner={isOwner} onTogglePin={(id) => togglePin(id).unwrap()} />
          ))}
          <SentinelLoader ref={discSentinelRef} isFetching={isFetching && discussions.length > 0} />
        </>
      )}

      {/* ── Stats tab ── */}
      {activeTab === 'stats' && (
        <div className={s.statsGrid}>
          <StatCard icon={statsData?.topCategory?.icon ?? '📂'} label="Favourite Topic" value={statsData?.topCategory?.name ?? '—'} sub={statsData?.topCategory ? `${statsData.topCategory.count} discussion${statsData.topCategory.count !== 1 ? 's' : ''}` : 'No discussions yet'} color={statsData?.topCategory?.color ?? undefined} />
          <Card
            padding="1.25rem"
            style={{ cursor: statsData?.bestDiscussion ? 'pointer' : 'default' }}
            onClick={() => statsData?.bestDiscussion && navigate(`/discussions/${statsData.bestDiscussion.id}`)}>
            <div className={s.statIcon}>🏅</div>
            <div className={s.statCardLabel}>Best Discussion</div>
            <div className={s.bestDiscTitle}>{statsData?.bestDiscussion?.title ?? '—'}</div>
            {statsData?.bestDiscussion && (
              <div className={s.bestDiscDetails}>
                <span>↑ {statsData.bestDiscussion.voteScore}</span>
                <span>💬 {statsData.bestDiscussion.commentCount}</span>
              </div>
            )}
          </Card>
          <StatCard icon="🤖" label="AI Responses Received" value={statsData?.aiResponseCount ?? '—'} sub="across all your discussions" color="var(--accent-primary)" />
          <StatCard icon="📈" label="Contributor Rank" value={statsData ? `Top ${100 - statsData.percentile + 1}%` : '—'} sub={statsData ? `among ${statsData.totalUsers} member${statsData.totalUsers !== 1 ? 's' : ''}` : ''} color={statsData && statsData.percentile >= 90 ? 'var(--warning)' : undefined} />
          <StatCard icon="🔥" label="Current Streak" value={streak >= 1 ? `${streak} day${streak !== 1 ? 's' : ''}` : 'No streak'} sub={streak >= 7 ? 'Keep it up!' : 'Post daily to build a streak'} color={streak >= 7 ? 'var(--warning)' : undefined} />
          <StatCard icon="✍️" label="All-time Contributions" value={activityData?.totalContributions ?? '—'} sub="discussions + comments" />
          {isOwner && aiUsageData && (
            <Card padding="1.25rem">
              <div className={s.statIcon}>⚡</div>
              <div className={s.statCardLabel}>AI Requests Today</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem', margin: '0.25rem 0' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: aiUsageData.remaining === 0 ? 'var(--status-error)' : aiUsageData.remaining <= 5 ? 'var(--warning)' : 'var(--text-primary)', lineHeight: 1 }}>
                  {aiUsageData.used}
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 400 }}>of {aiUsageData.limit}</span>
              </div>
              <div style={{ height: '6px', background: 'var(--bg-elevated)', borderRadius: '6px', overflow: 'hidden', margin: '0.5rem 0' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (aiUsageData.used / aiUsageData.limit) * 100)}%`, background: aiUsageData.remaining === 0 ? 'var(--status-error)' : aiUsageData.remaining <= 5 ? 'var(--warning)' : 'var(--accent-primary)', borderRadius: '6px', transition: 'width 0.4s ease' }} />
              </div>
              <div className={s.statCardSub} style={{ color: aiUsageData.remaining === 0 ? 'var(--status-error)' : 'var(--text-muted)' }}>
                {aiUsageData.remaining === 0 ? `Resets at ${new Date(aiUsageData.resetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })} IST` : `${aiUsageData.remaining} remaining today`}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── Badges tab ── */}
      {activeTab === 'achievements' && (
        <div>
          <div className={s.achievementsSection}>
            <div className={s.achievementsTitle}>🏆 Unlocked ({unlockedCount})</div>
            {unlockedCount === 0 ? (
              <div className={s.achievementsEmpty}>Start posting to earn your first badge!</div>
            ) : (
              <div className={s.achievementsList}>
                {achievements.filter(a => a.unlocked).map(a => <AchievementBadge key={a.id} a={a} />)}
              </div>
            )}
          </div>
          {achievements.some(a => !a.unlocked) && (
            <div>
              <div className={s.achievementsTitleMuted}>Locked ({achievements.filter(a => !a.unlocked).length})</div>
              <div className={s.achievementsList}>
                {achievements.filter(a => !a.unlocked).map(a => <AchievementBadge key={a.id} a={a} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── AI Conversations tab ── */}
      {activeTab === 'ai' && (
        <div>
          {aiFetching && !aiData && (
            <div className={s.aiCardSkeleton}>
              {[1,2,3].map(i => <div key={i} className={s.aiSkeletonItem} />)}
            </div>
          )}
          {aiData?.data.length === 0 && !aiFetching && (
            <EmptyState
              icon="🤖"
              title="No AI conversations yet"
              description="Post a question and our AI agents will answer instantly"
              action={<Link to="/new" className={s.aiAskLink}>Ask a question</Link>}
            />
          )}
          <div className={s.aiList}>
            {(aiData?.data ?? []).map(d => (
              <Link key={d.id} to={`/discussions/${d.id}`} className={s.noDecoration}>
                <div className={s.aiCard}>
                  <div className={s.aiCardInner}>
                    <span className={s.aiCardIcon}>{d.categories[0]?.icon ?? '💬'}</span>
                    <div className={s.aiCardInfo}>
                      <div className={s.aiCardTitle}>{d.title}</div>
                      {d.latestAiResponse && (
                        <div className={s.aiCardPreview}>
                          <span className={s.aiAgentName}>{d.latestAiResponse.agent?.name ?? 'AI'}:</span>
                          {contentPreview(d.latestAiResponse.content, AI_REPLY_PREVIEW_TRUNCATE)}
                        </div>
                      )}
                      <div className={s.aiCardMeta}>
                        {d.categories.slice(0, CATEGORY_BADGE_PREVIEW_MAX).map(c => <span key={c.name} style={{ color: c.color ?? 'var(--accent-primary)' }}>{c.icon} {c.name}</span>)}
                        <span>💬 {d.commentCount}</span>
                        <span>{timeAgoShort(d.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <SentinelLoader ref={aiSentinelRef} isFetching={aiFetching && (aiData?.data.length ?? 0) > 0} />
        </div>
      )}

      {/* ── Insights tab with sub-tabs ── */}
      {activeTab === 'insights' && (
        <div>

          {/* Sub-tab bar */}
          <div className={s.insightTabs}>
            {([
              { key: 'journey',   label: '🗺️ Journey' },
              { key: 'writing',   label: '✍️ Writing Style' },
              { key: 'active',    label: '🌙 Recently Active' },
              ...(onThisDayData && onThisDayData.discussions.length > 0 ? [{ key: 'onthisday', label: '📅 On This Day' }] : []),
            ] as { key: InsightTab; label: string }[]).map(t => (
              <button key={t.key} onClick={() => setInsightTab(t.key)}
                className={`${s.insightTab} ${insightTab === t.key ? s.active : ''}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* On This Day */}
          {insightTab === 'onthisday' && onThisDayData && onThisDayData.discussions.length > 0 && (
            <div className={s.onThisDayCard}>
              <div className={s.onThisDayHeader}>
                <span className={s.onThisDayIcon}>📅</span>
                <div>
                  <div className={s.onThisDayTitle}>On This Day</div>
                  <div className={s.onThisDaySub}>A year ago in {onThisDayData.year}</div>
                </div>
              </div>
              <div className={s.onThisDayList}>
                {onThisDayData.discussions.map(d => (
                  <Link key={d.id} to={`/discussions/${d.id}`} className={s.noDecoration}>
                    <div className={s.insightDiscRow}>
                      <span className={s.insightDiscIcon}>{d.categories[0]?.icon ?? '💬'}</span>
                      <div className={s.insightDiscInfo}>
                        <div className={s.insightDiscTitle}>{d.title}</div>
                        <div className={s.insightDiscMeta}>💬 {d.commentCount} · ↑ {d.voteScore}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Writing Style */}
          {insightTab === 'writing' && writingStats && (
            <div className={s.writingCard}>
              <div className={s.writingCardAccent} />
              <div className={s.writingCardHeader}>
                <div className={s.writingCardIconBox}>✍️</div>
                <div>
                  <div className={s.writingCardTitle}>Your Writing Style</div>
                  <div className={s.writingCardSub}>Based on {writingStats.totalPosts} discussion{writingStats.totalPosts !== 1 ? 's' : ''}</div>
                </div>
              </div>
              <div className={s.writingGrid}>
                <WritingStat icon="📏" label="Avg. words per post" value={`${writingStats.avgWords}`} unit="words" />
                {writingStats.peakDay && <WritingStat icon="📆" label="Most active day" value={writingStats.peakDay} />}
                {writingStats.topWord && <WritingStat icon="💡" label="Most-used word" value={`"${writingStats.topWord}"`} unit={`${writingStats.topWordCount}× used`} />}
                <WritingStat icon="📝" label="Total analysed" value={`${writingStats.totalPosts}`} unit={`discussion${writingStats.totalPosts !== 1 ? 's' : ''}`} />
              </div>
            </div>
          )}

          {/* Journey */}
          {insightTab === 'journey' && milestones.length > 0 && (
            <div className={s.journeyCard}>
              <div className={s.journeyCardAccent} />
              <div className={s.journeyCardHeader}>
                <div className={s.journeyCardIconBox}>🗺️</div>
                <div>
                  <div className={s.journeyCardTitle}>Your Journey</div>
                  <div className={s.journeyCardSub}>{milestones.length} milestone{milestones.length !== 1 ? 's' : ''} reached</div>
                </div>
              </div>

              {/* Horizontal stepper */}
              {(() => {
                const totalDiscs = (user as any)?._count?.discussions ?? 0
                const totalContribs = activityData?.totalContributions ?? 0
                const totalFollowers = (user as any)?._count?.followers ?? 0
                const nextMilestone = [
                  { achieved: totalContribs >= 10,  icon: '⏳', label: '10 Contributions', sub: `${10 - totalContribs} more to go` },
                  { achieved: totalDiscs >= 1,       icon: '⏳', label: 'First Discussion',  sub: 'Post your first discussion' },
                  { achieved: totalFollowers >= 1,   icon: '⏳', label: 'First Follower',    sub: 'Get your first follower' },
                  { achieved: totalDiscs >= 10,      icon: '⏳', label: '10 Discussions',   sub: `${Math.max(0, 10 - totalDiscs)} more to go` },
                  { achieved: totalFollowers >= 5,   icon: '⏳', label: '5 Followers',      sub: `${Math.max(0, 5 - totalFollowers)} more to go` },
                  { achieved: totalContribs >= 100,  icon: '⏳', label: '100 Contributions', sub: `${Math.max(0, 100 - totalContribs)} more to go` },
                  { achieved: totalFollowers >= 25,  icon: '⏳', label: '25 Followers',     sub: `${Math.max(0, 25 - totalFollowers)} more to go` },
                  { achieved: totalContribs >= 500,  icon: '⏳', label: 'Legend (500)',      sub: `${Math.max(0, 500 - totalContribs)} more to go` },
                ].find(m => !m.achieved)

                const allSteps = [
                  ...milestones.map(m => ({ ...m, pending: false })),
                  ...(nextMilestone ? [{ icon: nextMilestone.icon, label: nextMilestone.label, sub: nextMilestone.sub, pending: true }] : []),
                ]

                return (
                  <div className={s.stepperScroll}>
                    <div className={s.stepperTrack}>
                      {allSteps.map((step, i) => (
                        <div key={step.label} className={i < allSteps.length - 1 ? s.stepperItem : `${s.stepperItem} ${s.stepperItemLast}`}
                          style={{ flex: i < allSteps.length - 1 ? 1 : 0 }}>
                          <div className={s.stepperContent}>
                            <div className={`${s.stepCircle} ${step.pending ? s.pending : s.achieved}`}
                              style={{ opacity: step.pending ? 0.5 : 1 }}>
                              {step.icon}
                            </div>
                            <div className={s.stepLabel}>
                              <div className={s.stepLabelRow}>
                                <div className={`${s.stepName} ${step.pending ? s.pending : ''}`}>{step.label}</div>
                                {step.pending && <span className={s.stepNextBadge}>Next</span>}
                              </div>
                              {step.sub && <div className={s.stepSub}>{step.sub}</div>}
                              {(step as any).date && <div className={s.stepDate}>{(step as any).date}</div>}
                            </div>
                          </div>
                          {i < allSteps.length - 1 && (
                            <div className={s.stepConnector}
                              style={{ background: allSteps[i + 1]?.pending
                                ? 'repeating-linear-gradient(to right, var(--border) 0px, var(--border) 4px, transparent 4px, transparent 8px)'
                                : 'linear-gradient(to right, var(--accent-primary), rgba(124,58,237,0.3))'
                              }} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Recently Active */}
          {insightTab === 'active' && discussionsData && discussionsData.data.length > 0 && (
            <div className={s.recentlyActiveCard}>
              <div className={s.recentlyActiveHeader}>
                <span className={s.recentlyActiveIcon}>🌙</span>
                <div className={s.recentlyActiveTitle}>Recently Active Discussions</div>
              </div>
              <div className={s.recentlyActiveList}>
                {discussionsData.data.slice(0, 5).map(d => {
                  const lastActivity = new Date(d.updatedAt ?? d.createdAt)
                  const now = new Date()
                  const diffDays = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24))
                  const lastActiveLabel = diffDays === 0 ? 'Active today' : diffDays === 1 ? 'Active yesterday' : `Active ${diffDays}d ago`
                  return (
                    <Link key={d.id} to={`/discussions/${d.id}`} className={s.noDecoration}>
                      <div className={s.insightDiscRow}>
                        <span className={s.insightDiscIcon}>{(d as any).categories?.[0]?.icon ?? '💬'}</span>
                        <div className={s.insightDiscInfo}>
                          <div className={s.insightDiscTitle}>{d.title}</div>
                          <div className={s.insightDiscMeta}>💬 {d.commentCount} replies</div>
                        </div>
                        <span className={s.activityBadge} style={{
                          color: diffDays === 0 ? 'var(--success)' : diffDays <= 3 ? 'var(--accent-primary)' : 'var(--text-muted)',
                          background: diffDays === 0 ? 'rgba(34,197,94,0.1)' : diffDays <= 3 ? 'rgba(124,58,237,0.1)' : 'var(--bg-card)',
                        }}>
                          {lastActiveLabel}
                        </span>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>
          )}

          {/* Empty states */}
          {insightTab === 'writing' && !writingStats && (
            <EmptyState
              icon="✍️"
              title="No writing stats yet"
              description="Post a few discussions to see your writing style analysis"
            />
          )}
          {insightTab === 'journey' && milestones.length === 0 && (
            <EmptyState
              icon="✨"
              title="Your insights will appear here"
              description="Start posting to unlock your writing stats and journey timeline"
            />
          )}
        </div>
      )}
      {/* ── Follow list modal ── */}
      {followModal && (
        <FollowListModal
          userId={userId}
          initialMode={followModal}
          followerCount={(user as any)._count?.followers ?? 0}
          followingCount={(user as any)._count?.following ?? 0}
          onClose={() => setFollowModal(null)}
        />
      )}
    </div>
  )
}

function StatCard({ icon, label, value, sub, color }: {
  icon: string; label: string; value: string | number; sub?: string; color?: string
}) {
  return (
    <Card padding="1.25rem">
      <div className={s.statIcon}>{icon}</div>
      <div className={s.statCardLabel}>{label}</div>
      <div className={s.statCardValue} style={{ color: color ?? 'var(--text-primary)' }}>{value}</div>
      {sub && <div className={s.statCardSub}>{sub}</div>}
    </Card>
  )
}

function WritingStat({ icon, label, value, unit }: { icon: string; label: string; value: string; unit?: string }) {
  return (
    <div className={s.writingStat}>
      <span className={s.writingStatIcon}>{icon}</span>
      <div className={s.writingStatLabel}>{label}</div>
      <div className={s.writingStatValue}>{value}</div>
      {unit && <div className={s.writingStatUnit}>{unit}</div>}
    </div>
  )
}
